// src/i18n/dateFormat.ts
import { isAppLocale, type AppLocale } from './locales'

/**
 * 界面日期（含月份名 / 星期名）按当前语言格式化（2026-09-26 排班页 review LOW）。
 *
 * date-fns 的 `format(d, 'MMMM yyyy')` 不传 locale 永远是英文（排班页「September 2026」
 * 「Week of Sep 21」「MON」）；只补 `{ locale: zhCN }` 也不够 —— 模板里的词序是英文的，
 * 出来是「9月 21, 2026」。这里改用 Intl.DateTimeFormat 的字段选项，词序交给各语言自己：
 * en 与原来的 date-fns 模板逐字一致（September 2026 / Sep 21 / Sep 21, 2026 / Mon /
 * Monday, Sep 21, 2026 / Mon, September 21 / September 21, 2026），zh 是「2026年9月」「9月21日」。
 *
 * dayjs 不用这里：src/i18n/index.ts 切语言时已同步 dayjs 全局 locale；带时间的主路径
 * （formatDate / formatTime / formatDateTime…）在 src/utils/dateFormat.ts，按语言走 dayjs
 * localizedFormat（ll / LT / lll），只借用这里的 formatMonthDay 拼「月日 + 时间」的短格式。
 * 只有数字的格式（yyyy-MM-dd、HH:mm）不涉及语言，照旧用 date-fns。
 */
const BCP47: Record<AppLocale, string> = { en: 'en-US', zh: 'zh-CN', pt: 'pt-BR', es: 'es' }

export function intlLocale(locale: string | undefined): string {
  return isAppLocale(locale) ? BCP47[locale] : BCP47.en
}

/** 第三个参数 timeZone：不传按浏览器时区（排班页的日历日本来就是本地日）；传 IANA 名则按该时区取日历日。 */
const fmt = (locale: string | undefined, options: Intl.DateTimeFormatOptions, timeZone?: string) =>
  new Intl.DateTimeFormat(intlLocale(locale), timeZone ? { ...options, timeZone } : options)

/** September 2026 · 2026年9月 */
export const formatMonthYear = (d: Date, locale?: string, timeZone?: string) =>
  fmt(locale, { month: 'long', year: 'numeric' }, timeZone).format(d)
/** Sep 21 · 9月21日 */
export const formatMonthDay = (d: Date, locale?: string, timeZone?: string) =>
  fmt(locale, { month: 'short', day: 'numeric' }, timeZone).format(d)
/** Sep 21, 2026 · 2026年9月21日 */
export const formatMonthDayYear = (d: Date, locale?: string, timeZone?: string) =>
  fmt(locale, { month: 'short', day: 'numeric', year: 'numeric' }, timeZone).format(d)
/** September 21, 2026 · 2026年9月21日 */
export const formatLongDate = (d: Date, locale?: string, timeZone?: string) =>
  fmt(locale, { month: 'long', day: 'numeric', year: 'numeric' }, timeZone).format(d)
/** Mon · 周一 */
export const formatWeekdayShort = (d: Date, locale?: string, timeZone?: string) =>
  fmt(locale, { weekday: 'short' }, timeZone).format(d)
/** Mon, September 21 · 9月21日周一 */
export const formatWeekdayMonthDay = (d: Date, locale?: string, timeZone?: string) =>
  fmt(locale, { weekday: 'short', month: 'long', day: 'numeric' }, timeZone).format(d)
/** Monday, Sep 21, 2026 · 2026年9月21日星期一 */
export const formatFullDate = (d: Date, locale?: string, timeZone?: string) =>
  fmt(locale, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }, timeZone).format(d)

/**
 * 日期区间（只到日）：同年只写一次年份，年份放在该语言写年份的那一端，两端格式对称。
 * en「Oct 9 – Oct 12, 2026」· zh「2026年10月9日 – 10月12日」；跨年两端都带年份「Dec 30, 2026 – Jan 2, 2027」。
 * 不用 Intl formatRange：zh 会退化成「2026/4/12 – 2026/4/14」的纯数字格式。
 * timeZone：传 IANA 名则按该时区取日历日与年份（预订列表按用户时区）；不传按浏览器本地时区。
 */
export function formatDayRange(start: Date, end: Date, locale?: string, timeZone?: string): string {
  const yearOf = (d: Date) => fmt('en', { year: 'numeric' }, timeZone).format(d)
  if (yearOf(start) !== yearOf(end)) {
    return `${formatMonthDayYear(start, locale, timeZone)} – ${formatMonthDayYear(end, locale, timeZone)}`
  }
  const parts = fmt(locale, { month: 'short', day: 'numeric', year: 'numeric' }, timeZone).formatToParts(start)
  const yearFirst = parts.findIndex((p) => p.type === 'year') < parts.findIndex((p) => p.type === 'month')
  return yearFirst
    ? `${formatMonthDayYear(start, locale, timeZone)} – ${formatMonthDay(end, locale, timeZone)}`
    : `${formatMonthDay(start, locale, timeZone)} – ${formatMonthDayYear(end, locale, timeZone)}`
}
